import React from 'react';
import { Smartphone, WifiOff, Check, Shield, Users, Activity } from 'lucide-react';
import { COLORS } from './landingData';

export function OfflineDemo({ t, offlineDemo, setOfflineDemo }) {
  return (
    <section style={{ padding: '80px 24px', background: '#F7F5F2' }} data-testid="offline-section">
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Section Heading */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 40px)',
            fontWeight: 800, color: COLORS.text, lineHeight: 1.2, marginBottom: 12,
          }}>{t.offline.sectionTitle}</h2>
          <p style={{
            fontSize: 16, color: COLORS.textSecondary, lineHeight: 1.6,
            maxWidth: 600, margin: '0 auto',
          }}>{t.offline.sectionSubtitle}</p>
        </div>

        {/* iPad Frame */}
        <div data-testid="offline-ipad-frame" style={{
          background: '#2C2C2E', borderRadius: 28, padding: '18px 18px 28px',
          boxShadow: '0 30px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.06) inset',
          position: 'relative',
        }}>
          {/* iPad Screen */}
          <div style={{
            background: '#FAF8F5', borderRadius: 14, overflow: 'hidden',
            display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 420,
          }} className="offline-ipad-screen">

            {/* LEFT PANEL — MR Mobile App */}
            <div style={{ padding: '24px 20px 20px', borderRight: '1px solid #EDE9E3', transition: 'background 0.4s' }}>
              {/* App Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Smartphone style={{ width: 16, height: 16, color: '#333' }} />
                  <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 17, color: '#333' }}>{t.offline.mockup.header}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {offlineDemo ? (
                    <>
                      <WifiOff style={{ width: 14, height: 14, color: '#E57373' }} />
                      <span style={{
                        color: '#E57373', fontWeight: 600, fontSize: 13,
                        background: '#FFEFEF', padding: '3px 10px', borderRadius: 8,
                        letterSpacing: 0.3,
                      }}>{t.offline.mockup.offlineBadge}</span>
                    </>
                  ) : (
                    <>
                      <Check style={{ width: 14, height: 14, color: '#2E7D32' }} />
                      <span style={{
                        color: '#2E7D32', fontWeight: 600, fontSize: 13,
                        background: '#E8F5E9', padding: '3px 10px', borderRadius: 8,
                        letterSpacing: 0.3,
                      }}>{t.offline.mockup.onlineBadge}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Action List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { text: t.offline.mockup.item1, time: t.offline.mockup.item1time, offStatus: t.offline.mockup.item1status, onStatus: t.offline.mockup.item1synced, offDot: '#F59E0B', onDot: '#10B981' },
                  { text: t.offline.mockup.item2, time: t.offline.mockup.item2time, offStatus: t.offline.mockup.item2status, onStatus: t.offline.mockup.item2synced, offDot: '#F59E0B', onDot: '#10B981' },
                  { text: t.offline.mockup.item3, time: t.offline.mockup.item3time, offStatus: t.offline.mockup.item3status, onStatus: t.offline.mockup.item3status, offDot: '#10B981', onDot: '#10B981' },
                  { text: t.offline.mockup.item4, time: t.offline.mockup.item4time, offStatus: t.offline.mockup.item4status, onStatus: t.offline.mockup.item4synced, offDot: '#F59E0B', onDot: '#10B981' },
                ].map((item, i) => {
                  const isOff = offlineDemo;
                  const dot = isOff ? item.offDot : item.onDot;
                  const status = isOff ? item.offStatus : item.onStatus;
                  const isQueued = dot === '#F59E0B';
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0',
                      borderBottom: i < 3 ? '1px solid #F0ECE6' : 'none',
                      transition: 'opacity 0.3s',
                    }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0, transition: 'background 0.4s' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.text}</span>
                      </div>
                      <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap', marginRight: 8 }}>{item.time}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: isQueued && isOff ? '#F59E0B' : '#555',
                        background: isQueued && isOff ? '#FFF9E0' : '#E8F5E9',
                        padding: '2px 8px', borderRadius: 4, letterSpacing: 0.3, whiteSpace: 'nowrap',
                        transition: 'background 0.4s, color 0.4s',
                      }}>{status}</span>
                    </div>
                  );
                })}
              </div>

              {/* STILL RUNNING / ALL SYNCED Card */}
              <div style={{
                background: offlineDemo ? '#E8F5E9' : '#E3F2FD', borderRadius: 14, padding: '16px 18px', marginTop: 16,
                transition: 'background 0.4s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: offlineDemo ? '2px solid #2E7D32' : '2px solid #1565C0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 0.4s',
                  }}>
                    {offlineDemo
                      ? <Activity style={{ width: 14, height: 14, color: '#2E7D32' }} />
                      : <Check style={{ width: 14, height: 14, color: '#1565C0' }} />
                    }
                  </div>
                  <span style={{
                    fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: 15,
                    color: offlineDemo ? '#2E7D32' : '#1565C0', letterSpacing: 0.5,
                    transition: 'color 0.4s',
                  }}>{offlineDemo ? t.offline.mockup.stillRunning : t.offline.mockup.allSynced}</span>
                </div>
                <div style={{ fontSize: 13, color: '#4A4A4A', lineHeight: 1.5 }}>
                  {offlineDemo ? t.offline.mockup.stillRunningDesc : t.offline.mockup.allSyncedDesc}
                </div>
              </div>

              {/* SYNC STATUS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, padding: '10px 0' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#999', letterSpacing: 0.8 }}>{t.offline.mockup.syncStatus}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: offlineDemo ? '#2E7D32' : '#1565C0', transition: 'color 0.4s' }}>
                  {offlineDemo ? t.offline.mockup.syncPercent : t.offline.mockup.syncComplete}
                </span>
              </div>
              <div style={{ background: '#E0E0E0', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                <div style={{
                  width: offlineDemo ? '0%' : '100%', height: '100%',
                  background: offlineDemo ? '#2E7D32' : '#1565C0', borderRadius: 4,
                  transition: 'width 0.8s ease, background 0.4s',
                }} />
              </div>
            </div>

            {/* RIGHT PANEL — Info & Features */}
            <div style={{ padding: '24px 24px 20px', display: 'flex', flexDirection: 'column' }}>
              {/* Online/Offline Toggle */}
              <div data-testid="offline-toggle" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 14, fontWeight: offlineDemo ? 500 : 700, color: offlineDemo ? '#999' : '#2E7D32', transition: 'color 0.3s, font-weight 0.3s' }}>{t.offline.online}</span>
                <div
                  onClick={() => setOfflineDemo(!offlineDemo)}
                  style={{
                    width: 48, height: 26, borderRadius: 13,
                    background: offlineDemo ? '#E57373' : '#4CAF50',
                    position: 'relative', cursor: 'pointer',
                    transition: 'background 0.3s',
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: offlineDemo ? 'calc(100% - 24px)' : '2px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left 0.3s ease',
                  }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: offlineDemo ? 700 : 500, color: offlineDemo ? '#E57373' : '#999', transition: 'color 0.3s, font-weight 0.3s' }}>{t.offline.offlineLabel}</span>
              </div>

              {/* Info Card */}
              <div style={{
                background: '#fff', borderRadius: 14, padding: '22px 20px', marginBottom: 24,
                border: '1px solid #EDE9E3',
              }}>
                <h3 style={{
                  fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: 'clamp(18px, 2.2vw, 24px)',
                  color: '#333', marginBottom: 10, lineHeight: 1.3,
                }}>{offlineDemo ? t.offline.noConnection : t.offline.connected}</h3>
                <p style={{ fontSize: 13, color: '#616161', lineHeight: 1.65 }}>{offlineDemo ? t.offline.noConnectionDesc : t.offline.connectedDesc}</p>
              </div>

              {/* Feature List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
                <div data-testid="offline-feature-audit" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Shield style={{ width: 20, height: 20, color: '#2E7D32' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 15, color: '#333' }}>{t.offline.audit.title}</div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 1 }}>{t.offline.audit.desc}</div>
                  </div>
                </div>

                <div data-testid="offline-feature-access" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: '#E0F2F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users style={{ width: 20, height: 20, color: '#00796B' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 15, color: '#333' }}>{t.offline.access.title}</div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 1 }}>{t.offline.access.desc}</div>
                  </div>
                </div>

                <div data-testid="offline-feature-capability" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: '#FFF8E1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <WifiOff style={{ width: 20, height: 20, color: '#F57F17' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 15, color: '#333' }}>{t.offline.capability.title}</div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 1 }}>{t.offline.capability.desc}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* iPad Home Indicator */}
          <div style={{
            width: 100, height: 5, borderRadius: 3, background: '#555',
            margin: '12px auto 0',
          }} />
        </div>
      </div>
    </section>
  );
}
